import type { Board } from "../board";
import type { Direction } from "../direction";
import { Event } from "../events/event";
import { EventType } from "../events/eventType";
import {
	type Bumpable,
	type Interactable,
	isRemoteDoor,
	type LightSource,
	PRIORITY_DEFAULT,
	type ProcessableThing,
	type ProjectileHittable,
	RobotActorState,
	type SomethingWithState,
	type Tintable,
	type TintConfig,
} from "../interfaces";
import type { SimpleBoard } from "../levelgenerator/simpleBoard";
import { PhysicalThing } from "../physicalThing";
import { deserializeXY } from "../serialization";
import { Strings } from "../strings/strings";
import type { XY } from "../xy";
import { ZOrder } from "../zOrder";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";
import { BossPlowRobot } from "../mobs/bossPlowRobot.ts";

export enum PowerPlantSwitchState {
	OFF = "OFF",
	ON = "ON",
}

export class PowerPlantSwitch
	extends PhysicalThing
	implements ProcessableThing, Interactable, Bumpable, ProjectileHittable, SomethingWithState, Tintable, LightSource
{
	static key = "PowerPlantSwitch";

	private state: PowerPlantSwitchState = PowerPlantSwitchState.OFF;

	constructor(xy: XY) {
		super(xy, true);
	}

	getZOrder(): number {
		return ZOrder.INTERACTABLE_OBJECTS;
	}

	processTurn(board: Board): void {
		if (this.state !== PowerPlantSwitchState.OFF) return;
		const player = board.player;
		if (!player) return;
		if (!this.xy.isInSquareRadius(player.xy, 1)) return;
		const text = Strings.get("prompt_activatePowerPlant") ?? "prompt_activatePowerPlant";
		board.emitPrompt(text);
	}

	getProcessPriority(): number {
		return PRIORITY_DEFAULT;
	}

	canInteract(): boolean {
		return this.state === PowerPlantSwitchState.OFF;
	}

	interact(_player: PhysicalThing): void {
		this.activate();
	}

	bump(_direction: Direction, author: PhysicalThing): boolean {
		if (!this.canInteract()) return false;
		this.interact(author);
		return true;
	}

	isHittable(): boolean {
		return true;
	}

	onProjectileHit(_projectile: unknown): void {
		this.board!.addNonPhysicalThing(new Sound(SoundId.METAL_BANG, this.xy));
	}

	getState(): PowerPlantSwitchState {
		return this.state;
	}

	getTint(): TintConfig | null {
		if (this.state !== PowerPlantSwitchState.OFF) return null;
		return { r: 255, g: 255, b: 255, minAlpha: 0.0, maxAlpha: 0.3 };
	}

	getAnimationSpeedMultiplier(): number {
		return 1.0;
	}

	getLightInnerRadius(): number {
		return this.state === PowerPlantSwitchState.OFF ? 1 : 0;
	}

	getLightOuterRadius(): number {
		return this.state === PowerPlantSwitchState.OFF ? 4 : 0;
	}

	private activate(): void {
		const board = this.board!;
		this.state = PowerPlantSwitchState.ON;
		board.gameFlags.activatedPowerPlant = true;
		if (board.darkness > 0) board.darkness = 0;

		for (const thing of board.getPhysicalThings()) {
			if (isRemoteDoor(thing) && thing.doorId === "powerplantminiboss") {
				thing.open();
			}
			if (thing instanceof BossPlowRobot) {
				thing.setState(RobotActorState.ENGAGING);
			}
		}
		board.addNonPhysicalThing(new Sound(SoundId.POWER_PLANT_SWITCH_ACTIVATE, this.xy));
		board.addEvent(new Event(EventType.POWER_PLANT_ACTIVATED));
	}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), state: this.state };
	}

	static deserialize(data: any): PowerPlantSwitch {
		const s = new PowerPlantSwitch(deserializeXY(data.xy));
		s.visualId = data.visualId;
		s.state = data.state ?? PowerPlantSwitchState.OFF;
		return s;
	}

	static override initOnBoard(xy: XY, board: SimpleBoard, _props?: Record<string, unknown>): PhysicalThing[] {
		const s = new PowerPlantSwitch(xy);
		board.putThing(s);
		return [s];
	}
}
